import React, { useEffect, useState } from 'react';

import { Notification } from '@swyftx/aviary/atoms/Notification';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { FiatInput } from '@swyftx/aviary/molecules/FiatInput';

import AssetIcon from '@global-components/AssetIcon/AssetIcon';

import { Big } from '@shared/safe-big';
import { RatesStore } from '@shared/store';
import { formatCurrency } from '@shared/utils';

import { useCurrentPrice } from '@hooks/Trade/useCurrentPrice';
import {
  EstimatedLoanSummary,
  estimateLoanSummary,
  MAX_ESTIMATED_LOAN_AMOUNT,
  MAX_LVR,
  MIN_ESTIMATED_LOAN_AMOUNT,
} from '@utils/calculator';

import { useForm, Controller } from 'react-hook-form';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';
import { usePortfolioBalance } from 'src/lib/portfolio/hooks/usePortfolioBalance';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';

const MAX_INPUT_LIMIT = 5_000_000;

interface LoanAmountFormData {
  borrowPrincipalAmount: string;
  securityAmount?: string; // Optional, will be set based on calculations
}

const HowMuchWouldYouLikeToBorrowStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);
  const { getAssetByCode } = useMarkets();
  const { getBalance } = usePortfolioBalance();
  const { convertRate } = RatesStore.useRatesStore;

  const BTC = getAssetByCode('BTC');
  const AUD = getAssetByCode('AUD')!;

  // Assuming useCurrentPrice(AUD, BTC, 'midPrice') returns the price of 1 BTC in AUD
  // e.g., if 1 BTC = 50,000 AUD, currentPrice will be Big(50000)
  const currentPrice = Big(useCurrentPrice(AUD, BTC, 'midPrice'));
  const [securityAmount, setSecurityAmount] = useState<string>('');
  const [loanError, setLoanError] = useState<string | undefined>(undefined);
  const [availableBalance, setAvailableBalance] = useState<string | undefined>('0');
  const [loanSummary, setLoanSummary] = useState<EstimatedLoanSummary | undefined>(undefined);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<LoanAmountFormData>({
    mode: 'onChange',
    defaultValues: {
      borrowPrincipalAmount: applicationData.borrowPrincipalAmount?.toString() || '',
    },
  });

  const borrowPrincipalAmount = watch('borrowPrincipalAmount');

  const setAmounts = (loan: string, security: string) => {
    if (!loan || !security) {
      setValue('borrowPrincipalAmount', '');
      setSecurityAmount('');
      return;
    }
    if (Number(loan) > MAX_INPUT_LIMIT) {
      return;
    }
    setValue('borrowPrincipalAmount', loan);
    setSecurityAmount(security);
  };

  const onSubmit = (data: LoanAmountFormData) => {
    loanApplicationService.send({
      type: 'NEXT',
      data: { ...data, securityAmount: Big(securityAmount), borrowPrincipalAmount: Big(borrowPrincipalAmount) },
    });
  };

  useEffect(() => {
    if (!(AUD && BTC)) {
      return;
    }
    if (!(borrowPrincipalAmount || securityAmount)) {
      setLoanError(undefined);
      return;
    }

    if (Big(borrowPrincipalAmount || 0).gt(MAX_ESTIMATED_LOAN_AMOUNT)) {
      setLoanError(
        `Amount exceeds maximum limit of ${formatCurrency(
          MAX_ESTIMATED_LOAN_AMOUNT,
          AUD,
        )}. Please enter a lower amount.`,
      );
      return;
    }
    setLoanError(undefined);
  }, [AUD, BTC, borrowPrincipalAmount, securityAmount]);

  const handleLoanAmountChange = (value: string) => {
    // value is AUD loan amount
    if (!(BTC && AUD && currentPrice && currentPrice.gt(0))) {
      setAmounts(value, '0');
      return;
    }
    const btcEquivalent = convertRate(AUD, BTC, value, 'midPrice');
    setAmounts(value, btcEquivalent.div(MAX_LVR).toFixed(8));
  };

  useEffect(() => {
    if (BTC) {
      setAvailableBalance(getBalance(BTC.id)?.availableBalance ?? '0');
    }
    if ((borrowPrincipalAmount || securityAmount) && currentPrice && currentPrice.gt(0)) {
      setLoanSummary(
        estimateLoanSummary({
          fiat: borrowPrincipalAmount,
          rate: currentPrice.toFixed(2),
          availableBalance,
        }),
      );
    } else {
      setLoanSummary(undefined);
    }
  }, [BTC, borrowPrincipalAmount, securityAmount, getBalance, currentPrice, availableBalance]);

  const setMaxAmount = (type: 'FIAT' | 'CRYPTO') => {
    if (!BTC || !AUD || !currentPrice || currentPrice.eq(0)) {
      return;
    }

    const userBtcBalance = Big(getBalance(BTC.id)?.availableBalance ?? '0');
    const maxEstimatedLoanAmountBig = Big(MAX_ESTIMATED_LOAN_AMOUNT);

    if (type === 'FIAT') {
      // Max Loanable AUD = (User's BTC Balance * price of 1 BTC in AUD) * MAX_LVR
      const maxLoanableAudFromUserBtc = userBtcBalance.times(currentPrice).times(MAX_LVR);

      let audAmountToSet: Big;
      if (maxLoanableAudFromUserBtc.gt(maxEstimatedLoanAmountBig)) {
        audAmountToSet = maxEstimatedLoanAmountBig;
      } else {
        audAmountToSet = maxLoanableAudFromUserBtc;
      }

      if (audAmountToSet.lt(0)) {
        audAmountToSet = Big(0);
      }

      if (audAmountToSet.lt(MIN_ESTIMATED_LOAN_AMOUNT)) {
        setValue('borrowPrincipalAmount', '', { shouldValidate: true, shouldDirty: true });
        handleLoanAmountChange('');
        return;
      }

      const finalAudAmountString = audAmountToSet.toFixed(2);
      setValue('borrowPrincipalAmount', finalAudAmountString, { shouldValidate: true, shouldDirty: true });
      handleLoanAmountChange(finalAudAmountString);
    } else if (type === 'CRYPTO') {
      const potentialLoanFromAllUserBtc = userBtcBalance.times(currentPrice).times(MAX_LVR);

      let loanToSet: Big;
      let securityToSet: Big;

      if (potentialLoanFromAllUserBtc.lte(maxEstimatedLoanAmountBig)) {
        loanToSet = potentialLoanFromAllUserBtc;
        securityToSet = userBtcBalance;
      } else {
        loanToSet = maxEstimatedLoanAmountBig;
        securityToSet = loanToSet.div(currentPrice).div(MAX_LVR);
      }

      if (loanToSet.lt(0)) loanToSet = Big(0);
      if (securityToSet.lt(0)) securityToSet = Big(0);

      setAmounts(loanToSet.toFixed(2), securityToSet.toFixed(8));
    }
  };

  const needsMoreSecurity =
    loanSummary?.securityRequired && Big(loanSummary.securityRequired).gt(availableBalance ?? '0');

  return (
    <LoansStepContainer
      customTitle='How much cash would you like to borrow?'
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isValid || !!loanError}
    >
      <main className='flex flex-col gap-32'>
        <Body>
          Set the amount of cash you would like to borrow, and we&apos;ll show an estimate of how much crypto
          you&apos;ll need to provide as security.
        </Body>
        <div className='flex flex-col gap-8'>
          <Body weight='emphasis'>How much cash would you like to borrow?</Body>
          <Body>
            Borrow between {formatCurrency(MIN_ESTIMATED_LOAN_AMOUNT, AUD)} and{' '}
            {formatCurrency(MAX_ESTIMATED_LOAN_AMOUNT, AUD)}
          </Body>{' '}
          {/* TODO: dynamic limits */}
          <Controller
            name='borrowPrincipalAmount'
            control={control}
            rules={{
              required: 'Loan amount is required',
              validate: (value) => {
                const numValue = Big(value || 0);
                if (numValue.lt(MIN_ESTIMATED_LOAN_AMOUNT) && numValue.gt(0)) {
                  return `Minimum loan amount is ${formatCurrency(MIN_ESTIMATED_LOAN_AMOUNT, AUD)}`;
                }
                if (numValue.gt(MAX_ESTIMATED_LOAN_AMOUNT)) {
                  return `Maximum loan amount is ${formatCurrency(MAX_ESTIMATED_LOAN_AMOUNT, AUD)}`;
                }
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <FiatInput
                {...field}
                onChange={(value) => {
                  let processedValue = value;
                  if (Big(value || 0).gt(MAX_INPUT_LIMIT)) {
                    processedValue = String(MAX_INPUT_LIMIT);
                  }
                  field.onChange(processedValue);
                  handleLoanAmountChange(processedValue);
                }}
                asset={AUD}
                onMax={() => setMaxAmount('FIAT')}
                placeholder='0.00'
                errorMessage={loanError || error?.message}
              />
            )}
          />
        </div>
        <div className='flex flex-col gap-32'>
          <div>
            <Body weight='emphasis'>Estimated crypto required as security</Body>
            <Body>Security is only required if you&apos;re approved</Body>
          </div>
          <div className='flex flex-col gap-16'>
            <div className='flex flex-row items-center gap-8'>
              <AssetIcon asset={BTC} size={30} iconTiltDegrees={14} /> {/* TODO: grey on unchanged */}
              <Numeric size='xxlarge' weight='emphasis'>
                {`~ ${Big(loanSummary?.securityRequired || 0).toFixed(8)} BTC`}
              </Numeric>
            </div>
            <Numeric size='medium'>{`≈ ${
              AUD && loanSummary?.securityInFiat ? formatCurrency(loanSummary.securityInFiat, AUD) : '$0.00'
            } AUD`}</Numeric>
          </div>
        </div>

        <Separator dashed />

        <div className='flex flex-col gap-16'>
          <div className='flex items-center justify-between'>
            <div className='flex-grow'>
              {/* TODO: hover */}
              <span className='font-normal'>Available BTC:</span>
            </div>
            <div className='text-right'>
              <Numeric size='medium' weight='emphasis'>
                {BTC
                  ? formatCurrency(availableBalance ?? '0', BTC, {
                      appendCode: true,
                    })
                  : 'N/A'}
              </Numeric>
            </div>
          </div>

          {!!borrowPrincipalAmount &&
            Big(borrowPrincipalAmount).gt(0) &&
            !errors.borrowPrincipalAmount &&
            !loanError && (
              <Notification
                severity={needsMoreSecurity ? 'info' : 'success'}
                title={
                  needsMoreSecurity
                    ? `You'll need ~${Big(loanSummary?.securityRequired ?? '0')
                        .minus(availableBalance ?? '0')
                        .abs()
                        .toFixed(8)} BTC more for security`
                    : 'Your BTC balance covers the required security'
                }
              >
                <div className='flex flex-col gap-8'>
                  {needsMoreSecurity
                    ? "You can continue your application, and add the required BTC after you're approved."
                    : 'Based on the current market price, and once approved, you have enough BTC to secure this loan.'}
                </div>
              </Notification>
            )}
        </div>
      </main>
    </LoansStepContainer>
  );
};

export { HowMuchWouldYouLikeToBorrowStep };
